import { ActionContext, ActionTree } from 'vuex';
import { Stripe, StripeError } from '@stripe/stripe-js';
import { getStripePromise } from '@/utils/stripe-loader';
import { RootState } from '../../state';
import { StripeState } from './state';
import { MutationTypes } from './mutations';

/**
 * Get Stripe publishable key from environment variables
 * Uses VITE_STRIPE_LIVE_KEY and VITE_STRIPE_TEST_KEY for environment-specific configuration
 */
const getStripePublishableKey = (): string => {
  const isProduction = import.meta.env.VITE_PAYMENT_ENV === 'production';
  const key = isProduction
    ? import.meta.env.VITE_STRIPE_LIVE_KEY
    : import.meta.env.VITE_STRIPE_TEST_KEY;

  if (!key) {
    throw new Error(
      `Missing Stripe publishable key: VITE_STRIPE_${
        isProduction ? 'LIVE' : 'TEST'
      }_KEY not found in environment`
    );
  }

  return key;
};

// eslint-disable-next-line no-shadow
export enum ActionTypes {
  initializeStripe = 'initializeStripe',
  createCheckoutSession = 'createCheckoutSession',
  createInvoice = 'createInvoice'
}

type StripeActionCtx = ActionContext<StripeState, RootState>;

export interface StripeActions extends ActionTree<StripeState, RootState> {
  initializeStripe: (ctx: StripeActionCtx) => Promise<Stripe | null>;
  createCheckoutSession: (
    ctx: StripeActionCtx,
    payload: {
      lineItems: { priceId: string; quantity: number }[];
      successUrl?: string;
      cancelUrl: string;
      metadata?: Record<string, string>;
    }
  ) => Promise<{
    error: StripeError;
  } | void>;
  createInvoice: (
    ctx: StripeActionCtx,
    payload: { lineItems: { priceId: string; quantity: number }[] }
  ) => Promise<any>;
}

export const actions: StripeActions = {
  /**
   * Initialize Stripe with lazy loading
   * Only loads Stripe when this action is first called (when user accesses payment flow)
   * Implements singleton pattern to ensure only one instance is created
   */
  async initializeStripe({ state, commit }) {
    // Return cached instance if already loaded
    if (state.stripe) {
      return state.stripe;
    }

    // Return in-flight promise if already loading
    if (state.stripePromise) {
      return state.stripePromise;
    }

    try {
      commit(MutationTypes.SET_LOADING, true);

      const publishableKey = getStripePublishableKey();

      // Use latest Stripe API version (2025-10-29.clover)
      const stripePromise = getStripePromise(publishableKey, {
        apiVersion: '2025-10-29.clover' as any
      });

      commit(MutationTypes.SET_STRIPE_PROMISE, stripePromise);

      const stripe = await stripePromise;

      commit(MutationTypes.SET_STRIPE, stripe);
      commit(MutationTypes.SET_LOADING, false);

      return stripe;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize Stripe';
      commit(MutationTypes.SET_ERROR, errorMessage);
      throw error;
    }
  },

  async createCheckoutSession(
    { state, rootState, dispatch },
    { lineItems, successUrl, cancelUrl, metadata }
  ) {
    // Ensure Stripe is initialized before using it
    await dispatch(ActionTypes.initializeStripe);

    // Null check for user state
    const customerId = rootState.db.user?.stripeId ?? undefined;

    return fetch(`${import.meta.env.VITE_STRIPE_CHECKOUT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        lineItems: lineItems.map(item => ({
          price: item.priceId,
          quantity: item.quantity
        })),
        successUrl,
        cancelUrl,
        customerId,
        metadata
      })
    })
      .then(async response => {
        const session = await response.json();
        if (response.status === 400) throw session.error.raw.message;
        return state.stripe!.redirectToCheckout({ sessionId: session.id });
      })
      .catch(err => {
        console.log(err);
      });
  },

  async createInvoice({ rootState }, { lineItems }) {
    // Null check for user state
    const customerId = rootState.db.user?.stripeId ?? undefined;

    return fetch(import.meta.env.VITE_STRIPE_INVOICE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        lineItems: lineItems.map(item => ({
          price: item.priceId,
          quantity: item.quantity
        })),
        customerId
      })
    }).then(async response => {
      return response.json();
    });
  }
};
